import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ResponsiveContainer, BarChart, Bar, XAxis, Tooltip } from "recharts";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { fmtEUR, fmtEUR2, fmtDate } from "@/lib/format";
import { CARD, Loader, ErrorRetry } from "@/components/common";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";

const Badge = ({ children, tone = "gray" }) => <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${tone === "amber" ? "bg-amber-50 text-amber-700" : tone === "green" ? "bg-emerald-50 text-emerald-700" : "bg-gray-100 text-gray-600"}`}>{children}</span>;

function SupplierSheet({ k, onClose }) {
  const [d, setD] = useState(null);
  useEffect(() => { setD(null); if (k) { api.get(`/pilotage/suppliers/${encodeURIComponent(k)}`).then((r) => setD(r.data)).catch((e) => { toast.error(apiErr(e)); onClose(); }); } }, [k, onClose]);
  return (
    <Sheet open={!!k} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-xl" data-testid="supplier-sheet">
        <SheetTitle className="font-display text-xl">{d?.name || "Fournisseur"}</SheetTitle>
        {!d ? <Loader /> : (
          <div className="mt-4 space-y-5">
            <p className="text-xs text-gray-500">{d.siret ? `SIRET/SIREN ${d.siret}` : "SIRET non renseigné"} · client depuis {fmtDate(d.first_date)} · compte principal {d.main_account} {d.main_account_label}</p>
            <div className="grid grid-cols-3 gap-2 text-center">
              {[["Total HT", fmtEUR(d.total_ht)], ["Factures", d.count], ["Reste à régler", fmtEUR(d.unpaid_ttc)]].map(([l, v]) => (
                <div key={l} className="rounded-2xl bg-gray-50 p-3"><p className="text-[11px] text-gray-400">{l}</p><p className="font-bold tabular-nums">{v}</p></div>))}
            </div>
            <div className="h-40"><ResponsiveContainer width="100%" height="100%">
              <BarChart data={d.monthly.map((m) => ({ name: m.month.slice(2), HT: m.amount }))}><XAxis dataKey="name" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} /><Tooltip formatter={(v) => fmtEUR(v)} /><Bar dataKey="HT" fill="#B08D57" radius={[4, 4, 0, 0]} /></BarChart>
            </ResponsiveContainer></div>
            <div><p className="mb-2 text-sm font-semibold">Articles achetés</p>
              <div className="divide-y divide-gray-100 text-sm" data-testid="supplier-items">
                {d.items.map((it) => (
                  <div key={it.key} className="flex items-center justify-between gap-3 py-2">
                    <div className="min-w-0"><p className="truncate">{it.designation}</p><p className="text-xs text-gray-400">{it.count} achat(s) · min {fmtEUR2(it.min_price)} · max {fmtEUR2(it.max_price)}</p></div>
                    <div className="shrink-0 text-right"><p className="font-semibold tabular-nums">{fmtEUR2(it.last_price)}</p>{it.change_pct != null && <Badge tone={it.change_pct > 0 ? "amber" : "green"}>{it.change_pct > 0 ? "+" : ""}{it.change_pct} %</Badge>}</div>
                  </div>))}
                {!d.items.length && <p className="py-3 text-xs text-gray-400">Pas encore de lignes détaillées pour ce fournisseur.</p>}
              </div></div>
            <div><p className="mb-2 text-sm font-semibold">Factures</p>
              <div className="divide-y divide-gray-100 text-sm">
                {d.invoices.map((i) => (
                  <div key={i.id} className="flex items-center justify-between gap-3 py-2">
                    <span>{fmtDate(i.invoice_date)} · {i.invoice_number || "sans n°"} {i.is_credit_note && <Badge>Avoir</Badge>}</span>
                    <span className="flex items-center gap-2 tabular-nums">{fmtEUR2(i.is_credit_note ? -Math.abs(i.amount_ttc || 0) : i.amount_ttc)} <Badge tone={i.status === "reglee" ? "green" : "amber"}>{i.status === "reglee" ? "Réglée" : "À régler"}</Badge></span>
                  </div>))}
              </div></div>
          </div>)}
      </SheetContent>
    </Sheet>
  );
}

export function Suppliers() {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);
  const [err, setErr] = useState(false);
  const load = useCallback(() => { setErr(false); api.get("/pilotage/suppliers").then((r) => setRows(r.data)).catch(() => setErr(true)); }, []);
  const close = useCallback(() => setOpen(null), []);
  useEffect(() => { load(); }, [load]);
  const list = useMemo(() => (rows || []).filter((s) => !q || s.name.toLowerCase().includes(q.toLowerCase()) || s.siret.includes(q)), [rows, q]);
  if (err) return <ErrorRetry onRetry={load} />;
  if (!rows) return <Loader />;
  return (
    <div className="space-y-4">
      <Input placeholder="Rechercher un fournisseur ou un SIRET" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-sm rounded-full" data-testid="supplier-search" />
      <div className={`${CARD} divide-y divide-gray-100 overflow-hidden`} data-testid="supplier-list">
        {list.map((s) => (
          <button key={s.key} type="button" onClick={() => setOpen(s.key)} data-testid={`supplier-row-${s.key}`} className="flex w-full items-center justify-between gap-3 px-5 py-3.5 text-left transition-colors hover:bg-emerald-50/50">
            <div className="min-w-0"><p className="truncate font-semibold">{s.name}</p><p className="text-xs text-gray-400">{s.count} facture(s) · dernière le {fmtDate(s.last_date)} · {s.main_account} {s.main_account_label}</p></div>
            <div className="shrink-0 text-right"><p className="font-semibold tabular-nums">{fmtEUR(s.total_ht)} HT</p>{s.unpaid_ttc > 0 && <p className="text-xs text-amber-600">{fmtEUR(s.unpaid_ttc)} à régler</p>}</div>
          </button>))}
        {!list.length && <p className="p-8 text-center text-sm text-gray-400">Aucun fournisseur. Les fiches se créent automatiquement à partir de vos factures.</p>}
      </div>
      <SupplierSheet k={open} onClose={close} />
    </div>
  );
}
